import { DateTime } from 'luxon'
import { ScaleLabeling } from '../types/charts'

/**
 * Energy total shown in the history chart summary.
 *
 * The chart only has the average power of each bucket (day, week, month or
 * year), so the energy is the average power integrated over the bucket:
 * average power × hours in the bucket. The hour count comes from the bucket's
 * own start and end, so month lengths and DST changes are honoured, and the
 * bucket containing "now" is integrated only up to now.
 */

/** Scales whose buckets have a well-defined duration and get an energy total. */
export const ENERGY_TOTAL_SCALES: ScaleLabeling[] = ['day', 'week', 'month', 'year']

/**
 * Is an energy total shown for the given scale?
 */
export function hasEnergyTotal(steps: ScaleLabeling | undefined): boolean {
  return steps !== undefined && ENERGY_TOTAL_SCALES.includes(steps)
}

/**
 * Hours of the bucket [from, to) that have elapsed by `nowMs`: the whole
 * bucket for past buckets, the elapsed part for the current bucket and zero
 * for buckets that have not started yet.
 */
export function bucketHours(bucket: { from: number, to: number }, nowMs: number): number {
  const end = Math.min(bucket.to, nowMs)
  if (end <= bucket.from) {
    return 0
  }
  return DateTime.fromMillis(end).diff(DateTime.fromMillis(bucket.from), 'hours').hours
}

/**
 * Energy in kWh of a bucket from its average power in W, or null when there
 * is no average.
 */
export function bucketEnergyKwh(avgPowerW: number | null | undefined, bucket: { from: number, to: number } | undefined, nowMs: number): number | null {
  if (avgPowerW === null || avgPowerW === undefined || isNaN(avgPowerW) || bucket === undefined) {
    return null
  }
  return (avgPowerW / 1000) * bucketHours(bucket, nowMs)
}

/**
 * Format an energy total for the chart summary: decimal comma like the rest
 * of the summary, one decimal below 1000 kWh and none above, thousands
 * separated with a non-breaking space. High accuracy keeps three decimals.
 */
export function formatEnergyTotal(kwh: number | null, highAccuracy = false): string {
  if (kwh === null) {
    return '—'
  }
  const decimals = highAccuracy ? 3 : (Math.abs(kwh) < 1000 ? 1 : 0)
  const [whole, fraction] = Math.abs(kwh).toFixed(decimals).split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  const sign = kwh < 0 && Number(Math.abs(kwh).toFixed(decimals)) !== 0 ? '-' : ''
  return sign + grouped + (fraction ? ',' + fraction : '')
}
